import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { getAccommodationItems } from 'checkout/selectors/view/accommodation'
import { useAppSelector } from 'hooks/reduxHooks'
import useIsOfferPage from 'hooks/Navigation/useIsOfferPage'
import { isAccommodationV2Item, isBedbankItem, isLEHotelItem } from 'lib/checkout/checkoutUtils'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'

export type SalesSidebarStage = 'hidden' | 'offer' | 'checkout'

export interface SalesSidebarState {
  stage: SalesSidebarStage
  offerStay?: App.SalesCopilotOfferStay
}

const CHECKOUT_PATHS = ['/:regionCode/checkout/*']

function useSalesSidebar(): SalesSidebarState {
  const { pathname } = useLocation()
  const isOfferPage = useIsOfferPage()
  const offerStay = useAppSelector(state => state.aiOps.salesCopilot.offerStay)
  // Phase 1 covers hotels only (Flash, LPC and Bedbank), not tours, cruises or villas.
  const hasHotelInCart = useAppSelector(state => getAccommodationItems(state)
    .some(item => isLEHotelItem(item) || isBedbankItem(item) || isAccommodationV2Item(item)))

  const isCheckout = useMemo(
    () => !!matchLocaleAwarePath(pathname, CHECKOUT_PATHS) && !pathname.includes('/confirmation'),
    [pathname],
  )

  let stage: SalesSidebarStage = 'hidden'
  if (isCheckout && hasHotelInCart) {
    stage = 'checkout'
  } else if (isOfferPage && offerStay && pathname.includes(offerStay.offerId)) {
    // The stay stays in redux after leaving its offer, so only use it on that offer's page.
    stage = 'offer'
  }
  const visibleStay = stage === 'hidden' ? undefined : offerStay

  return useMemo(() => ({ stage, offerStay: visibleStay }), [stage, visibleStay])
}

export default useSalesSidebar
